/**
 * Files Page - Simple File List
 *
 * Browse, upload, and manage files stored in R2/S3
 */

import { useState } from 'react';
import {
  Typography,
  Empty,
  Spin,
  message,
  Card,
  Button,
  Upload,
  Table,
  Space,
  Modal,
  Input,
  Row,
  Col,
  Tooltip,
} from 'antd';
import {
  CloudOutlined,
  UploadOutlined,
  DownloadOutlined,
  DeleteOutlined,
  AppstoreOutlined,
  BarsOutlined,
  EyeOutlined,
} from '@ant-design/icons';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { storageApi, type TenantFile } from '@api/storage';
import FilePreview from '@components/files/FilePreview';
import FileThumbnail from '@components/files/FileThumbnail';
import type { ColumnsType } from 'antd/es/table';

const { Title, Text } = Typography;
const { Search } = Input;
const { confirm } = Modal;

export default function FilesPage() {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState('');
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('list');
  const [uploading, setUploading] = useState(false);
  const [previewFile, setPreviewFile] = useState<TenantFile | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);

  // Check if storage is configured
  const { data: storageConfig, isLoading: configLoading } = useQuery({
    queryKey: ['storage-config'],
    queryFn: () => storageApi.getConfig(),
  });

  // List all files
  const { data: filesData, isLoading: filesLoading } = useQuery({
    queryKey: ['files'],
    queryFn: () => storageApi.listFiles(),
    enabled: storageConfig?.is_active === true,
  });

  // Upload file mutation
  const uploadMutation = useMutation({
    mutationFn: (file: File) => storageApi.upload(file),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      message.success('File uploaded successfully');
    },
    onError: (error: any) => {
      message.error(error?.response?.data?.detail || 'Failed to upload file');
    },
  });

  // Delete file mutation
  const deleteMutation = useMutation({
    mutationFn: (id: string) => storageApi.deleteFile(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['files'] });
      message.success('File deleted successfully');
    },
    onError: () => {
      message.error('Failed to delete file');
    },
  });

  // Handle file upload
  const handleUpload = async (file: File) => {
    setUploading(true);
    try {
      await uploadMutation.mutateAsync(file);
    } finally {
      setUploading(false);
    }
    return false; // Prevent default upload
  };

  // Handle file delete
  const handleDelete = (file: TenantFile) => {
    confirm({
      title: `Delete ${file.original_name}?`,
      content: 'This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      onOk: () => deleteMutation.mutate(file.id),
    });
  };

  // Handle file preview
  const handlePreview = (file: TenantFile) => {
    console.log('handlePreview called with file:', file);
    // Transform file URL to use full API URL
    const fileWithFullUrl = {
      ...file,
      url: storageApi.getFileUrl(file),
    };
    console.log('Transformed URL:', file.url, '->', fileWithFullUrl.url);
    setPreviewFile(fileWithFullUrl);
    setPreviewOpen(true);
    console.log('Preview state set - open:', true, 'file:', file.original_name);
  };

  // Navigate to next/previous file in preview
  const handlePreviewNext = () => {
    if (!previewFile) return;
    const currentIndex = filteredFiles.findIndex(f => f.id === previewFile.id);
    if (currentIndex < filteredFiles.length - 1) {
      setPreviewFile(filteredFiles[currentIndex + 1]);
    }
  };

  const handlePreviewPrevious = () => {
    if (!previewFile) return;
    const currentIndex = filteredFiles.findIndex(f => f.id === previewFile.id);
    if (currentIndex > 0) {
      setPreviewFile(filteredFiles[currentIndex - 1]);
    }
  };

  // Format file size
  const formatSize = (bytes: number) => {
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    if (bytes < 1024 * 1024 * 1024) return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
    return `${(bytes / (1024 * 1024 * 1024)).toFixed(2)} GB`;
  };

  // Get file extension icon color (no longer needed with thumbnails, but kept for compatibility)
  // const getFileColor = (name: string) => {
  //   const ext = name.split('.').pop()?.toLowerCase();
  //   const colorMap: Record<string, string> = {
  //     pdf: '#ff4d4f',
  //     doc: '#1890ff',
  //     docx: '#1890ff',
  //     xls: '#52c41a',
  //     xlsx: '#52c41a',
  //     ppt: '#fa8c16',
  //     pptx: '#fa8c16',
  //     jpg: '#722ed1',
  //     jpeg: '#722ed1',
  //     png: '#722ed1',
  //     gif: '#722ed1',
  //     zip: '#8c8c8c',
  //     rar: '#8c8c8c',
  //   };
  //   return colorMap[ext || ''] || '#1890ff';
  // };

  // Filter files by search
  const filteredFiles = (filesData?.files || []).filter((file) =>
    file.original_name.toLowerCase().includes(search.toLowerCase())
  );

  // Table columns
  const columns: ColumnsType<TenantFile> = [
    {
      title: 'Name',
      dataIndex: 'original_name',
      key: 'name',
      ellipsis: true,
      render: (name: string, record) => (
        <Space>
          <FileThumbnail file={record} size={32} />
          <span>{name}</span>
        </Space>
      ),
      sorter: (a, b) => a.original_name.localeCompare(b.original_name),
    },
    {
      title: 'Size',
      dataIndex: 'size_bytes',
      key: 'size',
      width: 120,
      render: (size: number) => formatSize(size),
      sorter: (a, b) => a.size_bytes - b.size_bytes,
    },
    {
      title: 'Type',
      dataIndex: 'mime_type',
      key: 'type',
      width: 150,
      ellipsis: true,
      render: (type: string) => <Text type="secondary">{type}</Text>,
    },
    {
      title: 'Uploaded',
      dataIndex: 'created_at',
      key: 'created',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.created_at).getTime() - new Date(b.created_at).getTime(),
      defaultSortOrder: 'descend',
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 150,
      render: (_, record) => (
        <Space>
          <Tooltip title="Preview">
            <Button
              type="text"
              size="small"
              icon={<EyeOutlined />}
              onClick={() => handlePreview(record)}
            />
          </Tooltip>
          <Tooltip title="Download">
            <Button
              type="text"
              size="small"
              icon={<DownloadOutlined />}
              onClick={() => window.open(storageApi.getFileUrl(record), '_blank')}
            />
          </Tooltip>
          <Tooltip title="Delete">
            <Button
              type="text"
              size="small"
              danger
              icon={<DeleteOutlined />}
              onClick={() => handleDelete(record)}
              loading={deleteMutation.isPending}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  // Check if storage is configured
  if (configLoading) {
    return (
      <div style={{ padding: 24, textAlign: 'center' }}>
        <Spin size="large" />
        <div style={{ marginTop: 16 }}>Loading...</div>
      </div>
    );
  }

  if (!storageConfig?.is_active) {
    return (
      <div style={{ padding: 24 }}>
        <Title level={4}>File Manager</Title>
        <Empty
          image={<CloudOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
          description={
            <span>
              Storage not configured.
              <br />
              <Text type="secondary">
                Please configure R2/S3 storage in Admin Settings to enable file
                management.
              </Text>
            </span>
          }
        />
      </div>
    );
  }

  const files = filteredFiles;

  return (
    <div style={{ padding: 24 }}>
      {/* Header */}
      <div style={{ marginBottom: 24 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div>
            <Title level={4} style={{ margin: 0 }}>
              File Manager
            </Title>
            <Text type="secondary">
              {filesData?.total || 0} files • {formatSize(storageConfig?.storage_used_bytes || 0)} used
            </Text>
          </div>
          <Upload beforeUpload={handleUpload} showUploadList={false}>
            <Button type="primary" icon={<UploadOutlined />} loading={uploading}>
              Upload File
            </Button>
          </Upload>
        </div>

        {/* Search and View Toggle */}
        <div style={{ display: 'flex', gap: 12, marginTop: 16 }}>
          <Search
            placeholder="Search files..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            style={{ flex: 1, maxWidth: 400 }}
            allowClear
          />
          <Button.Group>
            <Button
              icon={<BarsOutlined />}
              type={viewMode === 'list' ? 'primary' : 'default'}
              onClick={() => setViewMode('list')}
            />
            <Button
              icon={<AppstoreOutlined />}
              type={viewMode === 'grid' ? 'primary' : 'default'}
              onClick={() => setViewMode('grid')}
            />
          </Button.Group>
        </div>
      </div>

      {/* Content */}
      {filesLoading ? (
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
          <div style={{ marginTop: 16 }}>
            <Text type="secondary">Loading files...</Text>
          </div>
        </div>
      ) : files.length === 0 ? (
        <Card>
          <Empty
            image={<CloudOutlined style={{ fontSize: 64, color: '#d9d9d9' }} />}
            description={search ? 'No files found' : 'No files yet'}
          >
            {!search && (
              <Upload beforeUpload={handleUpload} showUploadList={false}>
                <Button type="primary" icon={<UploadOutlined />}>
                  Upload Your First File
                </Button>
              </Upload>
            )}
          </Empty>
        </Card>
      ) : viewMode === 'list' ? (
        <Table
          columns={columns}
          dataSource={files}
          rowKey="id"
          pagination={{
            pageSize: 20,
            showSizeChanger: true,
            showTotal: (total) => `${total} files`,
          }}
        />
      ) : (
        <Row gutter={[16, 16]}>
          {files.map((file) => (
            <Col key={file.id} xs={12} sm={8} md={6} lg={4} xl={3}>
              <Card
                hoverable
                size="small"
                style={{ textAlign: 'center' }}
                styles={{ body: { padding: 12 } }}
                onClick={() => handlePreview(file)}
                actions={[
                  <Tooltip key="preview" title="Preview">
                    <EyeOutlined onClick={(e) => { e.stopPropagation(); handlePreview(file); }} />
                  </Tooltip>,
                  <Tooltip key="download" title="Download">
                    <DownloadOutlined onClick={(e) => { e.stopPropagation(); window.open(storageApi.getFileUrl(file), '_blank'); }} />
                  </Tooltip>,
                  <Tooltip key="delete" title="Delete">
                    <DeleteOutlined
                      style={{ color: '#ff4d4f' }}
                      onClick={(e) => { e.stopPropagation(); handleDelete(file); }}
                    />
                  </Tooltip>,
                ]}
              >
                <div style={{ marginBottom: 8 }}>
                  <FileThumbnail
                    file={file}
                    size={64}
                  />
                </div>
                <Tooltip title={file.original_name}>
                  <Text
                    ellipsis
                    style={{
                      display: 'block',
                      fontSize: 13,
                      fontWeight: 500,
                    }}
                  >
                    {file.original_name}
                  </Text>
                </Tooltip>
                <Text type="secondary" style={{ fontSize: 11 }}>
                  {formatSize(file.size_bytes)}
                </Text>
              </Card>
            </Col>
          ))}
        </Row>
      )}

      {/* File Preview Modal */}
      <FilePreview
        file={previewFile}
        open={previewOpen}
        onClose={() => {
          setPreviewOpen(false);
          setPreviewFile(null);
        }}
        onNext={handlePreviewNext}
        onPrevious={handlePreviewPrevious}
      />
    </div>
  );
}
